import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Delete, DriveFileMove } from '@mui/icons-material';

import { screen } from '$fcomponents/hoc';
import { dialog } from '$gcomponents/reusables';
import { Header, TableView2 } from '$gcomponents/widgets';
import ItemActionModel from '$gbusiness/models/itemAction';

import FILE_TABLE, { FILE_TABLE_CONFIG } from './table';
import { IonPageWrapper } from './styles';
import FileModal from './fileModal';
import { fileActions } from '$fbusiness/redux/file';
import FileEditModal from './fileModal/edit';
import StoreModel from '$fbusiness/models/store';
import FabButton from '$fcomponents/fabButton';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { generateSelectOptions } from '$gcomponents/utils/input';
import FILE_TYPE_OPTIONS, { FILE_TYPES } from '$fbusiness/enums/options/fileType';
import FolderBar, { folderSelectOptions } from './folderBar';
import FileFolderModel, { findFolder, folderTrail } from '$fbusiness/models/fileFolder';
import {
  createFolder,
  deleteFolder,
  fetchFolders,
  moveFilesToFolder,
  renameFolder,
} from '$fbusiness/redux/file/folderActions';
import PATH from '$business/enums/paths';
import { PageWrapper } from '$gstyles/wrapper';
import { Button } from '$gcomponents/primitives';
import intl from '$gintl';
import { Col, Row } from '$gstyles';
import { accessHide, getAccess, isAccessible } from '$fbusiness/helpers/util';

interface FilesScreenProps {
  fetchFile: Function;
  deleteFile: Function;
  cleanFiles: Function;
  stores: Array<StoreModel>;
  currentState;
}

const FilesScreen: React.FC<FilesScreenProps> = ({ stores, cleanFiles, deleteFile, currentState }) => {
  const location = useLocation();
  const isManualsView = location.pathname === PATH.MANUALS;
  const [filter, setFilter] = useState<any>({
    query: '',
    forceRefresh: true,
    category: FILE_TYPES.FILE,

  });
  const [showModal, setShowModal] = useState(false);
  const [selections, setSelections] = useState<any>(null);
  const [file, setFile] = useState<any>(null);
  const [folders, setFolders] = useState<Array<FileFolderModel>>([]);
  const [folderId, setFolderId] = useState<number | null>(null);
  const ACCESS = getAccess(currentState);

  const canDelete = isAccessible(ACCESS.ACTION.FILE.DELETE, currentState);
  const canEditFolders = isAccessible(ACCESS.ACTION.FILE.EDIT, currentState);

  const trail = folderTrail(folders, folderId);
  const currentFolder = findFolder(folders, folderId);

  const loadFolders = async () => {
    const tree = await fetchFolders();
    setFolders(tree);
    return tree;
  };

  useEffect(() => {
    (async () => {
      const tree = await loadFolders();
      // The Manuals nav item is a shortcut into a folder. Resolved by name once,
      // on entry -- the last name match left in the codebase. Move this to a
      // folder id stored in factory settings when the shortcut is made
      // configurable, so renaming the folder cannot break the link.
      if (isManualsView) {
        const manual = tree.find((f) => f.name === 'Manual');
        if (manual) onOpenFolder(manual.id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opening a folder filters the table to that folder and everything inside it,
  // so a file in Manual > Cake still shows when Manual is open.
  const onOpenFolder = (id: number | null) => {
    setFolderId(id);
    setSelections(null);
    updateFilter({ folderId: id || undefined });
  };

  const onCreateFolder = (parentId: number | null) => {
    dialog.prompt({
      title: 'New folder',
      message: 'Folder name',
      onPress: async (name) => {
        if (!name || !name.trim()) return;
        const res = await createFolder(name.trim(), parentId);
        if (!res.ok) {
          dialog.alert({ text: res.message });
          return;
        }
        await loadFolders();
      },
    });
  };

  const onRenameFolder = (folder: FileFolderModel) => {
    dialog.prompt({
      title: 'Rename folder',
      message: 'Folder name',
      defaultValue: folder.name,
      onPress: async (name) => {
        if (!name || !name.trim() || name.trim() === folder.name) return;
        const res = await renameFolder(folder.id, name.trim());
        if (!res.ok) {
          dialog.alert({ text: res.message });
          return;
        }
        await loadFolders();
      },
    });
  };

  // Non-empty folders are refused by the API on purpose. Rather than surface a
  // dead end, offer to re-file the contents and retry.
  const onDeleteFolder = async (folder: FileFolderModel) => {
    const res = await deleteFolder(folder.id);
    if (res.ok) {
      if (folderId === folder.id) onOpenFolder(folder.parentId);
      await loadFolders();
      return;
    }
    dialog.confirm({
      title: 'Folder is not empty',
      text: `${res.message} Move its contents to the top level and delete it?`,
      onPress: async () => {
        const retry = await deleteFolder(folder.id, null);
        if (!retry.ok) {
          dialog.alert({ text: retry.message });
          return;
        }
        if (folderId === folder.id) onOpenFolder(folder.parentId);
        await loadFolders();
        updateFilter({});
      },
    });
  };

  const onMoveFiles = () => {
    if (!selections?.length) return;
    dialog.select({
      title: `Move ${selections.length} file(s) to`,
      options: [{ value: 0, labelText: 'All files (top level)' }, ...folderSelectOptions(folders)],
      defaultValue: folderId || 0,
      confirmText: 'Move',
      onPress: async (destination) => {
        const ok = await moveFilesToFolder(selections, Number(destination) || null);
        if (!ok) dialog.alert({ text: 'Some files could not be moved.' });
        setSelections(null);
        await loadFolders();
        updateFilter({});
      },
    });
  };

  const onCloseModal = () => {
    setShowModal(false);
    setFile(null);
  };
  const onCloseEditModal = () => {
    setFile(null);
  };
  const onUpdateFile = () => {
    updateFilter({});
    onCloseModal();
  };

  const updateFilter = (newFilter) => {
    setFilter({
      ...filter,
      ...newFilter,
      forceRefresh: !filter.forceRefresh,
    });
  };

  const onSelection = (files) => {
    if (files.length) setSelections(files.filter((f) => f).map((f) => f.id));
    else setSelections(null);
  };

  const onDeleteFiles = () => {
    dialog.confirm({
      title: 'MESSAGE.WAIT',
      message: 'MESSAGE.DELETE_WARNING',
      onPress: async () => {
        for (const id of selections) {
          await deleteFile(id);
        }
        updateFilter({ resetSelection: true });
        setSelections(null);
      },
    });
  };

  const onCleanFiles = () => {
    dialog.confirm({
      title: 'MESSAGE.WAIT',
      message: 'MESSAGE.FILE_CLEAN_WARNING',
      onPress: async () => {
        await cleanFiles();
        updateFilter({});
      },
    });
  };

  const itemActions: Array<ItemActionModel> = [
    {
      label: 'BUTTON.DELETE',
      eventName: 'deleteItem',
      disabled: () => !canDelete,
      onClick: (row) => {
        dialog.confirm({
          message: 'MESSAGE.DELETE_WARNING',
          onPress: async () => {
            await deleteFile(row.id);
            updateFilter({});
          },
        });
      },
    },
    {
      label: 'BUTTON.EDIT',
      eventName: 'edit',
      disabled: () => !isAccessible(ACCESS.ACTION.FILE.EDIT, currentState),
      onClick: async (row) => {
        setFile(row);
      },
    },
  ];

  return (
    <IonPageWrapper>
      <Header title={isManualsView ? 'SCREEN.FILES.TITLE_MANUALS' : 'SCREEN.FILES.TITLE'} />
      <PageWrapper>
        <Row>
          <Col gridSize={4}>
            <FormControl size="small" variant="outlined">
              <InputLabel id="cat-select">File Category</InputLabel>
              <Select
                size="small"
                id="cat-select"
                className="small cat-filter"
                variant="outlined"
                label="File Category"
                value={filter.category}
                onChange={(e) => updateFilter({ category: e.target.value })}>
                {generateSelectOptions(FILE_TYPE_OPTIONS, true)}
              </Select>
            </FormControl>
          </Col>
          <Col gridSize={8}>
            <div className="pull-right">
              <Button
                onClick={onCleanFiles}
                size="small"
                className={`pull-right ${accessHide(ACCESS.ACTION.FILE.CLEAN)}`}
                color="secondary">
                {intl('SCREEN.FILES.BUTTON_CLEAN')}
              </Button>
              {/* <Button onClick={onCleanFiles} size="small" className="pull-right" color="error">
                {intl('SCREEN.FILES.BUTTON_PURGE')}
              </Button> */}
            </div>
          </Col>
        </Row>
      </PageWrapper>

      {filter.category === FILE_TYPES.FILE && (
        <PageWrapper>
          <FolderBar
            folders={folders}
            trail={trail}
            current={currentFolder}
            maxDepth={3}
            canEdit={canEditFolders}
            onOpen={onOpenFolder}
            onCreate={onCreateFolder}
            onRename={onRenameFolder}
            onDelete={onDeleteFolder}
          />
        </PageWrapper>
      )}

      <TableView2
        onSelection={canDelete ? onSelection : undefined}
        tableConfig={FILE_TABLE_CONFIG}
        TABLE={FILE_TABLE(filter.category)}
        filter={filter}
        itemActions={itemActions}
      />
      <FileModal
        show={showModal}
        onClose={onCloseModal}
        onUpdateFile={onUpdateFile}
        stores={stores}
        defaultFolderId={folderId}
      />
      <FileEditModal
        show={!!file}
        onClose={onCloseEditModal}
        file={file}
        stores={stores}
        onUpdateFile={onUpdateFile}
      />
      {filter.category === FILE_TYPES.FILE && (
        <FabButton className={accessHide(ACCESS.ACTION.FILE.CREATE)} onClick={() => setShowModal(true)} />
      )}
      {selections && canEditFolders && (
        <FabButton
          ver="bottom"
          hor="start"
          className={accessHide(ACCESS.ACTION.FILE.EDIT)}
          icon={<DriveFileMove />}
          onClick={onMoveFiles}
        />
      )}
      {selections && (
        <FabButton
          ver="bottom"
          className={accessHide(ACCESS.ACTION.FILE.DELETE)}
          icon={<Delete />}
          color="danger"
          onClick={onDeleteFiles}></FabButton>
      )}
    </IonPageWrapper>
  );
};

const mapStateToProps = (state) => ({
  stores: state.store.stores,
  factory: state.factory.factory,
  resetOnRoute: true,
});

const mapDispatchToProps = {
  fetchFile: fileActions.fetchFile,
  deleteFile: fileActions.deleteFile,
  cleanFiles: fileActions.cleanupFiles,
  updateFile: fileActions.updateFile,
};

const connected = connect(mapStateToProps, mapDispatchToProps);

export default connected(screen(FilesScreen));
